import { Component, inject, OnInit } from '@angular/core';
import { DxDataGridComponent, DxFormComponent, DxTemplateDirective, } from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSearchPanelComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { FilterPanelComponent } from '../../../components/library/filter-panel/filter-panel.component';
import {
    YardManagementClient,
    YardCapacityGridViewDto,
    AuthService,
    YardEntityType,
    AppointmentStatus,
    EmptyCheckInStatus,
    EmptyCheckOutStatus,
} from '@services';
import { CommonModule } from '@angular/common';
import { DATE_TIME_FORMAT } from 'src/app/shared/constants';
import { FilterConfig, GridPageComponent } from '../../common/grid-page.component';
import { Router, RouterLink } from '@angular/router';
import { CapitalCasePipe } from 'src/app/pipes/capital-case.pipe';
import { AppointmentStatusPipe } from 'src/app/pipes/appointment-status.pipe';
import { EmptyCheckInStatusPipe } from 'src/app/pipes/empty-check-in-status.pipe';
import { EmptyCheckOutStatusPipe } from 'src/app/pipes/empty-check-out-status.pipe';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';

@Component({
    selector: 'app-yard-capacity',
    imports: [
        CommonModule,
        CapitalCasePipe,
        DxDataGridComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        DxoSortingComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoSearchPanelComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        FilterPanelComponent,
        DxFormComponent,
        RouterLink,
    ],
    providers: [AppointmentStatusPipe, EmptyCheckInStatusPipe, EmptyCheckOutStatusPipe],
    templateUrl: './yard-capacity.component.html',
})
export class YardCapacityComponent extends GridPageComponent<YardCapacityGridViewDto> implements OnInit
{
    private router = inject(Router);
    private yardManagementClient = inject(YardManagementClient);
    protected auth = inject(AuthService);
    private appointmentStatusPipe = inject(AppointmentStatusPipe);
    private emptyCheckInStatusPipe = inject(EmptyCheckInStatusPipe);
    private emptyCheckOutStatusPipe = inject(EmptyCheckOutStatusPipe);

    public YardEntityType = YardEntityType;
    protected override exportName: string = 'Yard Capacity Report';
    protected offset: number = 100;
    dateTimeFormat = DATE_TIME_FORMAT;
    dataSource: YardCapacityGridViewDto[] = [];

    statusOptions: Array<{ value: string; text: string }> = [
        ...[
            AppointmentStatus.CheckedIn,
            AppointmentStatus.ShuntMovePending,
            AppointmentStatus.ReadyToUnload,
            AppointmentStatus.Investigation,
            AppointmentStatus.Unloading,
            AppointmentStatus.TagsAsnPgiRequired,
            AppointmentStatus.Refused,
        ].map(value => ({ value, text: this.appointmentStatusPipe.transform(value) })),
        ...[
            EmptyCheckInStatus.EmptyRequired,
            EmptyCheckInStatus.EmptyNotRequired,
            EmptyCheckInStatus.Loading,
            EmptyCheckInStatus.Loaded,
        ].map(value => ({ value, text: this.emptyCheckInStatusPipe.transform(value) })),
        ...[
            EmptyCheckOutStatus.ReadyForCheckout,
            EmptyCheckOutStatus.ReadyForCheckoutEmpty,
            EmptyCheckOutStatus.ReadyForCheckoutLoaded,
        ].map(value => ({ value, text: this.emptyCheckOutStatusPipe.transform(value) })),
    ];

    protected override filterConfig: FilterConfig[] = [
        { field: 'number', op: 'contains' },
        { field: 'carrier', op: 'contains' },
        { field: 'trailerNumber', op: 'contains' },
        { field: 'currentShuntLocation', op: 'contains' },
        { field: 'pendingShuntLocation', op: 'contains' },
        { field: 'status', op: '=' },
        { field: 'created', op: '>=' },
    ];

    constructor() {
        super();
        this.init(YardCapacityGridViewDto);
    }

    ngOnInit(): void {
        this.loadData();
    }

    protected getStatusBadgeClass(status: string | undefined): string {
        switch (status) {
            case "Approved":
                return 'bg-success-subtle text-success';
            case "CheckedIn":
                return 'bg-info-subtle text-info';
            case "NoShow":
                return 'bg-danger-subtle text-danger';
            case "ReadyToUnload":
                return 'bg-info-subtle text-info';
            case "Unloading":
                return 'bg-info-subtle text-info';
            case "TagsAsnPgiRequired":
                return 'bg-warning-subtle text-warning';
            case "Refused":
                return 'bg-danger-subtle text-danger';
            case "EmptyRequired":
                return 'bg-success-subtle text-success';
            case "EmptyNotRequired":
                return 'bg-danger-subtle text-danger';
            case "ShuntMovePending":
                return 'bg-warning-subtle text-warning';
            case "Investigation":
                return 'bg-warning-subtle text-warning';
            case "Loading":
            case "Loaded":
                return 'bg-success-subtle text-success';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    }

    async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        switch (e.data.yardEntityType) {        
            case YardEntityType.Appointment:
                await this.router.navigate([`/inbound/appointments/details/${e.data.number}`]);
                break;
            case YardEntityType.EmptyCheckIn:
                await this.router.navigate([`/inbound/empty-check-ins/details/${e.data.number}`]);
                break;
            case YardEntityType.EmptyCheckOut:
                await this.router.navigate([`/outbound/empty-check-outs/details/${e.data.number}`]);
                break;
        }
    }

    loadData() {
        this.yardManagementClient.getYardCapacity().subscribe({
            next: (data) => {
                this.dataSource = data;
            },
        });
    }
}
